// Where does this visitor need a consent banner before any tracker loads?
//
// Detection is the browser's own IANA timezone, NOT an IP lookup. An IP-geo
// service would mean shipping every visitor's address to a third party in order
// to decide whether we're allowed to track them, which is a strange way to run
// a privacy control — and it costs a network round trip on first paint, before
// we can decide whether to show the banner.
//
// The trade-off is accuracy: a Berlin visitor on a US VPN reads as US and gets
// auto-granted. Every ambiguity therefore resolves TOWARD showing the banner
// (unknown timezone, unreadable Intl, no window: all require consent), and the
// zone list below is deliberately over-inclusive — it catches all of Europe,
// including non-EU countries where a banner is merely unnecessary rather than
// wrong. Over-showing costs a click; under-showing is a GDPR breach.

// EU/EEA/UK/CH territories whose IANA zone does not start with "Europe/".
// Mostly the French overseas departments and the Iberian/Nordic Atlantic
// islands, all of which GDPR covers.
const NON_EUROPE_PREFIX_ZONES = new Set([
  'Atlantic/Canary',      // Spain
  'Atlantic/Madeira',     // Portugal
  'Atlantic/Azores',      // Portugal
  'Atlantic/Reykjavik',   // Iceland (EEA)
  'Atlantic/Faroe',       // Denmark
  'Atlantic/Jan_Mayen',   // Norway (EEA)
  'America/Cayenne',      // French Guiana
  'America/Guadeloupe',   // Guadeloupe
  'America/Martinique',   // Martinique
  'America/Marigot',      // Saint Martin
  'America/St_Barthelemy',
  'America/Miquelon',     // Saint Pierre and Miquelon
  'Indian/Reunion',       // Réunion
  'Indian/Mayotte',       // Mayotte
  'Asia/Nicosia',         // Cyprus
  'Asia/Famagusta',       // Cyprus
  'Antarctica/Troll',     // Norwegian station
]);

export function getTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

// True when this visitor must be asked before any non-essential cookie is set.
// Fails safe: anything we cannot positively place outside Europe counts as yes.
export function consentRequiredHere() {
  if (typeof window === 'undefined') return true;
  const tz = getTimeZone();
  if (!tz) return true;
  return tz.startsWith('Europe/') || NON_EUROPE_PREFIX_ZONES.has(tz);
}

// Global Privacy Control: the browser-level opt-out signal that California
// (CPRA), Colorado and Connecticut require businesses to honor. A visitor
// outside the consent-banner regions is auto-granted, so this is the one thing
// standing between a GPC user and marketing tags they explicitly refused.
export function gpcEnabled() {
  if (typeof navigator === 'undefined') return false;
  return navigator.globalPrivacyControl === true;
}
